/**
 * Reference: MeControl doc: https://mecontroltestpartner.azurewebsites.net/PartnerDocs/articles/guides/dualStack-authProvider.html
 */

import { IAuthProviderConfig } from '@mecontrol/public-api';
import { AuthProviderConfigType } from '@mecontrol/public-api/types/AuthProviderConfig';

interface IAuthProviderContext {
  readonly isMsalJsEnabled: boolean;
  readonly evoBase: string;
  readonly appId: string;
}

interface IAuthProviderCallbacks {
  readonly onSignOut: () => void;
  readonly onSwitch?: () => void;
  readonly onSwitchTo?: (upn: string) => void;
}

const UX_LOGOUT = 'uxlogout';
const FORGET_USER = 'forgetuser';
const SAVED_USER = 'savedusers';
const WREPLY = 'wreply';
const APPID = 'appId';
const SHOULD_FORGET_USER = 'shouldForgetUser';

export const addCDN = (src: string, onLoadHandler: ((this: GlobalEventHandlers, ev: Event) => any) | null) => {
  const script = document.createElement('script');
  script.src = src;
  script.onload = onLoadHandler;
  document.body.appendChild(script);
};

export const buildSignOutUrl = (evoEndpoint: string, appId: string) => {
  const url: URL = new URL(evoEndpoint);
  url.pathname = UX_LOGOUT;
  url.searchParams.set(APPID, appId);
  url.searchParams.set(WREPLY, encodeURI(`https://${location.host}/account/complete-signin-aad`));
  url.searchParams.set(SHOULD_FORGET_USER, 'false');
  return url;
};

export const buildSignOutAndForgetUrl = (evoEndpoint: string, appId: string) => {
  const url = buildSignOutUrl(evoEndpoint, appId);
  url.searchParams.set(SHOULD_FORGET_USER, 'true');
  return url;
};

export const buildForgetUrl = (evoEndpoint: string) => {
  const url: URL = new URL(evoEndpoint);
  url.pathname = FORGET_USER;
  return url;
};

export const buildRememberedAccountsUrl = (evoEndpoint: string, appId: string) => {
  const url: URL = new URL(evoEndpoint);
  url.pathname = SAVED_USER;
  url.searchParams.set(APPID, appId);
  url.searchParams.set(WREPLY, encodeURI(`https://${location.host}/account/complete-signin-aad`));
  return url;
};

export const getAuthProviderConfig = (
  { isMsalJsEnabled, evoBase, appId }: IAuthProviderContext,
  { onSignOut, onSwitch, onSwitchTo }: IAuthProviderCallbacks
): IAuthProviderConfig => ({
  type: isMsalJsEnabled
    ? ('msalJsWithMsa' as AuthProviderConfigType.MsalJsWithMsa) // casting here and other lines below is because the exported enum is a const enum, which will be undefined in the run time if directly use it
    : ('adalJsWithMsaFed' as AuthProviderConfigType.AdalJsWithMsaFed),
  signInTo: args => `?upn=${args.nextAccount.memberName}`,
  signOutFromApp: () => {
    onSignOut();
  },
  switch: onSwitch ? () => onSwitch() : undefined,
  switchTo: onSwitchTo
    ? args => {
        onSwitchTo(args.nextAccount.memberName);
      }
    : undefined,
  signIn: () => `/`,
  aad: {
    signOutUrl: buildSignOutUrl(evoBase, appId).toString(),
    signOutAndForgetUrl: buildSignOutAndForgetUrl(evoBase, appId).toString(),
    forgetUrl: buildForgetUrl(evoBase).toString(),
    rememberedAccountsUrl: buildRememberedAccountsUrl(evoBase, appId).toString()
  }
});
